(function() {
  "use strict";

  if (typeof window === "undefined" || window.__cartRateLimiterInstalled) {
    return;
  }

  window.__cartRateLimiterInstalled = true;

  var config = window.__cartRateLimiterConfig || {};
  var minIntervalMs = Number(config.minIntervalMs);
  var maxRetries = Number(config.maxRetries);
  var disableFlagKey = typeof config.disableFlagKey === "string" ? config.disableFlagKey : "disableCartLimiter";
  var dedupeGetCart = config.dedupeGetCart !== false;
  var dedupeWindowMs = Number(config.dedupeWindowMs);
  var reloadOn429 = config.reloadOn429 !== false;
  var reloadCooldownMs = Number(config.reloadCooldownMs);
  var captchaReloadSessionKey = typeof config.captchaReloadSessionKey === "string" ? config.captchaReloadSessionKey : "cartLimiter429ReloadAt";
  var debug = config.debug === true;

  if (!Number.isFinite(minIntervalMs) || minIntervalMs < 0) {
    minIntervalMs = 1000;
  }

  if (!Number.isFinite(maxRetries) || maxRetries < 0) {
    maxRetries = 2;
  }

  if (!Number.isFinite(dedupeWindowMs) || dedupeWindowMs < 0) {
    dedupeWindowMs = 3000;
  }

  if (!Number.isFinite(reloadCooldownMs) || reloadCooldownMs < 0) {
    reloadCooldownMs = 15000;
  }

  var queue = [];
  var isProcessing = false;
  var lastCompletedAt = 0;
  var inflightGetRequests = new Map();
  var inflightGetPayloads = new Map();
  var recentGetResponses = new Map();
  var cartDataCacheEpoch = 0;
  var hasTriggeredReload = false;

  function log() {
    if ((!debug && window.__cartRateLimiterDebug !== true) || !window.console || typeof window.console.log !== "function") {
      return;
    }

    var args = Array.prototype.slice.call(arguments);
    args.unshift("[cart-rate-limiter]");
    window.console.log.apply(window.console, args);
  }

  function isDisabled() {
    try {
      if (window.__disableCartLimiter === true) {
        return true;
      }

      if (!window.localStorage) {
        return false;
      }

      return window.localStorage.getItem(disableFlagKey) === "1";
    } catch (error) {
      return window.__disableCartLimiter === true;
    }
  }

  function toAbsoluteUrl(urlLike) {
    try {
      return new URL(urlLike, window.location.href);
    } catch (error) {
      return null;
    }
  }

  function normalizeMethod(method) {
    return String(method || "GET").toUpperCase();
  }

  function getFetchUrl(input) {
    if (typeof input === "string") {
      return input;
    }

    if (input && typeof input.url === "string") {
      return input.url;
    }

    return "";
  }

  function getFetchMethod(input, init) {
    if (init && init.method) {
      return normalizeMethod(init.method);
    }

    if (input && input.method) {
      return normalizeMethod(input.method);
    }

    return "GET";
  }

  function getFetchSignal(input, init) {
    if (init && init.signal) {
      return init.signal;
    }

    if (input && input.signal) {
      return input.signal;
    }

    return null;
  }

  function isCartPath(pathname) {
    return /(^|\/)cart(?=\/|\.|$)/i.test(pathname || "");
  }

  function isCartDataPath(pathname) {
    return /\/cart\.(?:js|json)\/?$/i.test(pathname || "");
  }

  function normalizeCartDataPath(pathname) {
    return String(pathname || "").replace(/\/cart\.(?:js|json)\/?$/i, "/cart.__data__");
  }

  function isCartRequest(urlLike) {
    var absoluteUrl = toAbsoluteUrl(urlLike);

    if (!absoluteUrl || !isAllowedCartOrigin(absoluteUrl.origin)) {
      return false;
    }

    return isCartPath(absoluteUrl.pathname);
  }

  function isCartMutationRequest(method, pathname) {
    var normalizedMethod = normalizeMethod(method);
    var normalizedPath = String(pathname || "");

    if (normalizedMethod === "GET" || normalizedMethod === "HEAD" || normalizedMethod === "OPTIONS") {
      return false;
    }

    if (!isCartPath(normalizedPath)) {
      return false;
    }

    return /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/i.test(normalizedPath) || /\/cart\/?$/i.test(normalizedPath);
  }

  function isAllowedCartOrigin(origin) {
    if (origin === window.location.origin) {
      return true;
    }

    if (typeof window.Shopify !== "undefined" && typeof window.Shopify.shop === "string" && window.Shopify.shop) {
      if (origin === "https://" + window.Shopify.shop) {
        return true;
      }
    }

    return false;
  }

  function makeRequestKey(method, absoluteUrl) {
    var normalizedOrigin = absoluteUrl.origin;
    var normalizedPathname = absoluteUrl.pathname;
    var normalizedSearch = absoluteUrl.search;

    if (method === "GET" && isCartDataPath(normalizedPathname)) {
      normalizedOrigin = "__cart_data_origin__";
      normalizedPathname = normalizeCartDataPath(normalizedPathname);
      normalizedSearch = "";
    }

    return method + " " + normalizedOrigin + normalizedPathname + normalizedSearch;
  }

  function createAbortError() {
    try {
      return new DOMException("The operation was aborted.", "AbortError");
    } catch (error) {
      var abortError = new Error("The operation was aborted.");
      abortError.name = "AbortError";
      return abortError;
    }
  }

  function wait(ms, signal) {
    if (!ms || ms <= 0) {
      return Promise.resolve();
    }

    return new Promise(function(resolve, reject) {
      if (signal && signal.aborted) {
        reject(createAbortError());
        return;
      }

      var timeoutId = setTimeout(onFinish, ms);

      function onAbort() {
        cleanup();
        reject(createAbortError());
      }

      function onFinish() {
        cleanup();
        resolve();
      }

      function cleanup() {
        clearTimeout(timeoutId);

        if (signal) {
          signal.removeEventListener("abort", onAbort);
        }
      }

      if (signal) {
        signal.addEventListener("abort", onAbort, { once: true });
      }
    });
  }

  function getRetryDelayMs(retryAfter, attempt) {
    var fallback = Math.min(5000, 600 * Math.pow(2, attempt)) + Math.floor(Math.random() * 150);

    if (!retryAfter) {
      return fallback;
    }

    var secondsValue = Number(retryAfter);

    if (Number.isFinite(secondsValue) && secondsValue >= 0) {
      return Math.max(fallback, secondsValue * 1000);
    }

    var retryAt = Date.parse(retryAfter);

    if (!Number.isNaN(retryAt)) {
      return Math.max(fallback, retryAt - Date.now());
    }

    return fallback;
  }

  function getLastReloadAt() {
    try {
      if (!window.sessionStorage) {
        return 0;
      }

      var raw = Number(window.sessionStorage.getItem(captchaReloadSessionKey));
      return Number.isFinite(raw) && raw > 0 ? raw : 0;
    } catch (error) {
      return 0;
    }
  }

  function setLastReloadAt(timestamp) {
    try {
      if (!window.sessionStorage) {
        return;
      }

      window.sessionStorage.setItem(captchaReloadSessionKey, String(timestamp));
    } catch (error) {
      return;
    }
  }

  function triggerCaptchaRefresh(reason) {
    if (!reloadOn429 || hasTriggeredReload) {
      return;
    }

    var now = Date.now();
    var lastReloadAt = getLastReloadAt();

    if (lastReloadAt > 0 && now - lastReloadAt < reloadCooldownMs) {
      log("429 detected, skipped reload during cooldown", reason);
      return;
    }

    hasTriggeredReload = true;
    setLastReloadAt(now);
    log("429 detected, reloading for bot verification", reason);

    setTimeout(function() {
      window.location.reload();
    }, 0);
  }

  function shouldRefreshOnStatus(status) {
    var normalizedStatus = Number(status);
    return normalizedStatus === 429 || normalizedStatus === 403;
  }

  function waitForFetchResponseBody(response) {
    if (!response || typeof response.clone !== "function") {
      return Promise.resolve(response);
    }

    try {
      return response.clone().text().then(function() {
        return response;
      }, function() {
        return response;
      });
    } catch (error) {
      return Promise.resolve(response);
    }
  }

  function enqueue(task) {
    return new Promise(function(resolve, reject) {
      queue.push({ task: task, resolve: resolve, reject: reject });
      processQueue();
    });
  }

  function getRecentResponseClone(key) {
    if (dedupeWindowMs <= 0) {
      return null;
    }

    var entry = recentGetResponses.get(key);

    if (!entry) {
      return null;
    }

    if (Date.now() - entry.timestamp > dedupeWindowMs) {
      recentGetResponses.delete(key);
      return null;
    }

    return entry.response.clone();
  }

  function getRecentPayloadPromise(key) {
    if (dedupeWindowMs <= 0) {
      return null;
    }

    var entry = recentGetResponses.get(key);

    if (!entry) {
      return null;
    }

    if (Date.now() - entry.timestamp > dedupeWindowMs) {
      recentGetResponses.delete(key);
      return null;
    }

    return entry.payloadPromise || null;
  }

  function createPayloadFromResponsePromise(responsePromise) {
    return Promise.resolve(responsePromise)
      .then(function(response) {
        if (!response) {
          return null;
        }

        var payloadSource = response.clone();
        var contentType = payloadSource.headers && payloadSource.headers.get ? payloadSource.headers.get("content-type") : "application/json";

        return payloadSource.text().then(function(bodyText) {
          return {
            bodyText: bodyText,
            contentType: contentType || "application/json"
          };
        });
      })
      .catch(function() {
        return null;
      });
  }

  function createResponseFromPayload(payload) {
    if (!payload || typeof payload.bodyText !== "string") {
      return null;
    }

    var contentType = payload.contentType || "application/json";
    var status = Number(payload.status);
    var statusText = payload.statusText || "OK";

    if (!Number.isFinite(status) || status <= 0) {
      status = 200;
    }

    try {
      return new Response(payload.bodyText, {
        status: status,
        statusText: statusText,
        headers: {
          "Content-Type": contentType
        }
      });
    } catch (error) {
      return null;
    }
  }

  function setRecentPayload(key, payload) {
    if (dedupeWindowMs <= 0 || !payload || typeof payload.bodyText !== "string") {
      return;
    }

    var response = createResponseFromPayload(payload);
    if (!response) {
      return;
    }

    recentGetResponses.set(key, {
      timestamp: Date.now(),
      response: response,
      payloadPromise: Promise.resolve({
        bodyText: payload.bodyText,
        contentType: payload.contentType || "application/json",
        status: Number(payload.status) || 200,
        statusText: payload.statusText || "OK"
      })
    });
  }

  function setRecentPayloadWithEpoch(key, payload, cacheEpoch) {
    if (cacheEpoch !== cartDataCacheEpoch) {
      return;
    }

    setRecentPayload(key, payload);
  }

  function setRecentResponse(key, response, cacheEpoch) {
    if (cacheEpoch !== cartDataCacheEpoch) {
      return;
    }

    if (dedupeWindowMs <= 0) {
      return;
    }

    var cachedResponse = response.clone();

    recentGetResponses.set(key, {
      timestamp: Date.now(),
      response: cachedResponse,
      payloadPromise: createPayloadFromResponsePromise(response)
    });
  }

  function invalidateCartDataCache(reason) {
    cartDataCacheEpoch += 1;
    inflightGetRequests.clear();
    inflightGetPayloads.clear();
    recentGetResponses.clear();
    log("invalidate cart cache", reason || "unknown");
  }

  function processQueue() {
    if (isProcessing) {
      return;
    }

    var next = queue.shift();

    if (!next) {
      return;
    }

    isProcessing = true;
    var waitMs = Math.max(0, minIntervalMs - (Date.now() - lastCompletedAt));

    setTimeout(function() {
      Promise.resolve()
        .then(next.task)
        .then(next.resolve, next.reject)
        .finally(function() {
          lastCompletedAt = Date.now();
          isProcessing = false;
          processQueue();
        });
    }, waitMs);
  }

  if (typeof window.fetch === "function") {
    var originalFetch = window.fetch.bind(window);

    window.fetch = function(input, init) {
      if (isDisabled()) {
        return originalFetch(input, init);
      }

      var method = getFetchMethod(input, init);
      var requestUrl = getFetchUrl(input);
      var absoluteUrl = toAbsoluteUrl(requestUrl);

      if (!absoluteUrl || !isCartRequest(absoluteUrl.href)) {
        return originalFetch(input, init);
      }

      if (isCartMutationRequest(method, absoluteUrl.pathname)) {
        invalidateCartDataCache("fetch " + absoluteUrl.pathname);
      }

      var signal = getFetchSignal(input, init);

      var executeFetch = function() {
        var attempt = 0;

        function run() {
          if (signal && signal.aborted) {
            return Promise.reject(createAbortError());
          }

          return originalFetch(input, init).then(function(response) {
            if (!shouldRefreshOnStatus(response.status)) {
              return waitForFetchResponseBody(response);
            }

            triggerCaptchaRefresh("fetch " + absoluteUrl.pathname);

            if (reloadOn429 || attempt >= maxRetries) {
              return waitForFetchResponseBody(response);
            }

            var retryAfter = response.headers && response.headers.get ? response.headers.get("Retry-After") : null;
            var delayMs = getRetryDelayMs(retryAfter, attempt);
            attempt += 1;
            log("429 detected, retrying", absoluteUrl.pathname, "attempt", attempt, "delay", delayMs);

            return wait(delayMs, signal).then(run);
          });
        }

        return run();
      };

      if (dedupeGetCart && method === "GET") {
        var key = makeRequestKey(method, absoluteUrl);
        var cacheEpoch = cartDataCacheEpoch;
        var recentResponse = getRecentResponseClone(key);

        if (recentResponse) {
          log("fetch cache hit", absoluteUrl.href);
          return Promise.resolve(recentResponse);
        }

        if (inflightGetRequests.has(key)) {
          log("fetch inflight hit", absoluteUrl.href);
          return inflightGetRequests.get(key).then(function(response) {
            return response.clone();
          });
        }

        if (inflightGetPayloads.has(key)) {
          log("fetch xhr inflight hit", absoluteUrl.href);
          return inflightGetPayloads.get(key).then(function(payload) {
            var response = createResponseFromPayload(payload);
            if (response) {
              return response;
            }

            return enqueue(executeFetch).then(function(fallbackResponse) {
              setRecentResponse(key, fallbackResponse, cacheEpoch);
              return fallbackResponse;
            });
          });
        }

        log("fetch network", absoluteUrl.href);
        var queuedRequest = enqueue(executeFetch)
          .then(function(response) {
            setRecentResponse(key, response, cacheEpoch);
            return response;
          })
          .finally(function() {
            inflightGetRequests.delete(key);
          });

        inflightGetRequests.set(key, queuedRequest);

        return queuedRequest.then(function(response) {
          return response.clone();
        });
      }

      return enqueue(executeFetch);
    };
    window.fetch.__cartRateLimited = true;
  }

  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var xhrProto = window.XMLHttpRequest.prototype;
    var originalXhrOpen = xhrProto.open;
    var originalXhrSend = xhrProto.send;
    var originalXhrAbort = xhrProto.abort;

    function getXhrPayload(xhr) {
      if (xhr.readyState !== 4 || xhr.status < 200 || xhr.status >= 300) {
        return null;
      }

      var responseType = xhr.responseType;

      if (responseType && responseType !== "text" && responseType !== "") {
        return null;
      }

      return {
        bodyText: xhr.responseText || "",
        contentType: xhr.getResponseHeader("Content-Type") || "application/json",
        status: xhr.status,
        statusText: xhr.statusText
      };
    }

    function finishXhrInflightPayload(xhr, state, cartDataKey, cacheEpoch, forceNull) {
      if (!state.resolveInflightCartPayload) {
        return;
      }

      var resolvePayload = state.resolveInflightCartPayload;
      state.resolveInflightCartPayload = null;

      var payload = null;

      if (!forceNull && cacheEpoch === cartDataCacheEpoch) {
        payload = getXhrPayload(xhr);
        if (payload) {
          setRecentPayloadWithEpoch(cartDataKey, payload, cacheEpoch);
        }
      }

      if (cartDataKey) {
        inflightGetPayloads.delete(cartDataKey);
      }

      resolvePayload(payload);
    }

    function startXhrInflightPayload(state, cartDataKey) {
      if (!cartDataKey || inflightGetPayloads.has(cartDataKey)) {
        return;
      }

      var resolvePayload;
      var payloadPromise = new Promise(function(resolve) {
        resolvePayload = resolve;
      });

      state.resolveInflightCartPayload = resolvePayload;
      inflightGetPayloads.set(cartDataKey, payloadPromise);
    }

    function enqueueXhrNetworkSend(xhr, state, cartDataKey, cacheEpoch) {
      enqueue(function() {
        return new Promise(function(resolve, reject) {
          if (state.abortedBeforeSend) {
            finishXhrInflightPayload(xhr, state, cartDataKey, cacheEpoch, true);
            resolve();
            return;
          }

          function cleanup() {
            xhr.removeEventListener("loadend", onLoadEnd);
          }

          function onLoadEnd() {
            cleanup();
            finishXhrInflightPayload(xhr, state, cartDataKey, cacheEpoch, false);
            resolve();
          }

          xhr.addEventListener("loadend", onLoadEnd, { once: true });

          try {
            state.sent = true;
            originalXhrSend.call(xhr, state.body);
          } catch (error) {
            cleanup();
            finishXhrInflightPayload(xhr, state, cartDataKey, cacheEpoch, true);
            reject(error);
          }
        });
      }).catch(function(error) {
        if (error && error.name === "AbortError") {
          return;
        }

        log("XHR enqueue failed", error);
      });
    }

    function sendXhrNetwork(xhr, state, cartDataKey, cacheEpoch) {
      startXhrInflightPayload(state, cartDataKey);
      enqueueXhrNetworkSend(xhr, state, cartDataKey, cacheEpoch);
    }

    function sendXhrFromPayloadPromise(xhr, state, payloadPromise, cartDataKey, cacheEpoch) {
      function sendNetworkFallback() {
        sendXhrNetwork(xhr, state, cartDataKey, cacheEpoch);
      }

      Promise.resolve(payloadPromise)
        .then(function(payload) {
          if (state.abortedBeforeSend) {
            return;
          }

          if (!payload || typeof payload.bodyText !== "string") {
            sendNetworkFallback();
            return;
          }

          try {
            var blob = new Blob([payload.bodyText], { type: payload.contentType || "application/json" });
            var blobUrl = URL.createObjectURL(blob);
            state.syntheticBlobUrl = blobUrl;

            xhr.addEventListener("loadend", function revokeBlobUrl() {
              if (state.syntheticBlobUrl) {
                URL.revokeObjectURL(state.syntheticBlobUrl);
                state.syntheticBlobUrl = null;
              }
            }, { once: true });

            state.sent = true;
            originalXhrOpen.call(xhr, "GET", blobUrl, true);
            originalXhrSend.call(xhr, null);
            log("served duplicate xhr cart request from cache", state.url);
          } catch (error) {
            if (state.syntheticBlobUrl) {
              try {
                URL.revokeObjectURL(state.syntheticBlobUrl);
              } catch (revokeError) {
              }
              state.syntheticBlobUrl = null;
            }

            sendNetworkFallback();
          }
        })
        .catch(function() {
          if (state.abortedBeforeSend) {
            return;
          }
          sendNetworkFallback();
        });
    }

    xhrProto.open = function(method, url) {
      this.__cartLimiterState = this.__cartLimiterState || {};
      this.__cartLimiterState.method = normalizeMethod(method);
      this.__cartLimiterState.url = typeof url === "string" ? url : String(url || "");
      this.__cartLimiterState.isAsync = arguments.length < 3 ? true : arguments[2] !== false;
      this.__cartLimiterState.isCart = isCartRequest(this.__cartLimiterState.url);
      this.__cartLimiterState.sent = false;
      this.__cartLimiterState.abortedBeforeSend = false;
      return originalXhrOpen.apply(this, arguments);
    };

    xhrProto.send = function(body) {
      var state = this.__cartLimiterState;

      if (!state || !state.isCart || !state.isAsync || isDisabled()) {
        return originalXhrSend.apply(this, arguments);
      }

      var xhr = this;
      state.body = body;
      var absoluteUrl = toAbsoluteUrl(state.url);
      if (absoluteUrl && isCartMutationRequest(state.method, absoluteUrl.pathname)) {
        invalidateCartDataCache("xhr " + absoluteUrl.pathname);
      }
      var shouldDedupeCartData = dedupeGetCart && state.method === "GET" && absoluteUrl && isCartDataPath(absoluteUrl.pathname);
      var cartDataKey = shouldDedupeCartData ? makeRequestKey(state.method, absoluteUrl) : null;
      var cacheEpoch = cartDataCacheEpoch;

      if (cartDataKey && !state.hasCacheCaptureListener) {
        state.hasCacheCaptureListener = true;
        xhr.addEventListener("loadend", function() {
          setRecentPayloadWithEpoch(cartDataKey, getXhrPayload(xhr), cacheEpoch);
        });
      }

      if (!state.hasStatusListener) {
        state.hasStatusListener = true;
        xhr.addEventListener("readystatechange", function() {
          if (xhr.readyState === 4 && shouldRefreshOnStatus(xhr.status)) {
            triggerCaptchaRefresh("xhr " + state.url);
          }
        });
      }

      if (cartDataKey) {
        var recentPayloadPromise = getRecentPayloadPromise(cartDataKey);

        if (recentPayloadPromise) {
          log("xhr cache hit", state.url);
          sendXhrFromPayloadPromise(xhr, state, recentPayloadPromise, cartDataKey, cacheEpoch);
          return;
        }

        if (inflightGetPayloads.has(cartDataKey)) {
          log("xhr payload inflight hit", state.url);
          sendXhrFromPayloadPromise(xhr, state, inflightGetPayloads.get(cartDataKey), cartDataKey, cacheEpoch);
          return;
        }

        if (inflightGetRequests.has(cartDataKey)) {
          log("xhr inflight hit", state.url);
          sendXhrFromPayloadPromise(
            xhr,
            state,
            createPayloadFromResponsePromise(inflightGetRequests.get(cartDataKey)),
            cartDataKey,
            cacheEpoch
          );
          return;
        }
      }

      if (cartDataKey) {
        log("xhr network", state.url);
      }
      sendXhrNetwork(xhr, state, cartDataKey, cacheEpoch);
    };

    xhrProto.abort = function() {
      var state = this.__cartLimiterState;

      if (state && state.isCart && !state.sent) {
        state.abortedBeforeSend = true;
        return;
      }

      return originalXhrAbort.apply(this, arguments);
    };

    xhrProto.__cartRateLimiterPatched = true;
  }
})();
